import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'
import type { SessionMarkers } from '../../lib/hardware/sessionAggregator'
import { useHardwareMetricLabels } from '../../hooks/useHardwareMetricLabels'

type PostSessionReviewProps = {
  markers: SessionMarkers
  strainScore: number | null
  machineName?: string
  onClose?: () => void
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

const PostSessionReview = ({ markers, strainScore, machineName, onClose }: PostSessionReviewProps) => {
  const { t } = useTranslation()
  const { metricLabels } = useHardwareMetricLabels()
  const displayName = machineName ?? markers.machineKind
  const strainLabel = strainScore != null ? strainScore.toFixed(1) : '—'

  const items = useMemo(
    () => [
      { label: t('workout.review.duration'), value: formatDuration(markers.durationSeconds) },
      {
        label: t('workout.review.peakPower'),
        value: markers.peakPowerWatts > 0 ? `${markers.peakPowerWatts} W` : '—',
      },
      {
        label: t('workout.review.avgPower'),
        value: markers.avgPowerWatts > 0 ? `${markers.avgPowerWatts} W` : '—',
      },
      {
        label: t('workout.review.peakVelocity'),
        value: markers.peakVelocityMs > 0 ? `${markers.peakVelocityMs} m/s` : '—',
      },
      {
        label: t('workout.review.maxHr'),
        value: markers.maxHeartRate > 0 ? `${markers.maxHeartRate} bpm` : '—',
      },
      {
        label: t('workout.review.avgHr'),
        value: markers.avgHeartRate > 0 ? `${markers.avgHeartRate} bpm` : '—',
      },
      {
        label: t('workout.review.distance'),
        value: markers.totalDistanceM > 0 ? `${markers.totalDistanceM} m` : '—',
      },
      {
        label: t('workout.review.calories'),
        value: markers.totalCalories > 0 ? `${markers.totalCalories} kcal` : '—',
      },
    ],
    [markers, t],
  )

  const peakHr = markers.maxHeartRate
  const peakSplit = markers.machineKind === 'rower' || markers.machineKind === 'ski_erg'

  return (
    <div className={`${KILO.bg.card} ${KILO.border.default} ${KILO.radius.card} p-6`}>
      <p className={KILO.meta}>{t('workout.review.title')}</p>

      <div className="mt-4 flex items-end gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-kilo-accent">{t('workout.review.trainingLoad')}</p>
          <p className="font-black tabular-nums text-white" style={{ fontSize: '3.5rem', lineHeight: 1 }}>
            {strainLabel}
          </p>
          <p className="text-xs text-neutral-500">{t('workout.review.strainOf')}</p>
        </div>
        <div className="mb-2 h-16 w-px bg-neutral-800" />
        <p className="mb-2 flex-1 text-sm leading-relaxed text-neutral-300">
          {t('workout.review.narrative', { machine: displayName, strain: strainLabel })}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="rounded-xl border border-neutral-800 bg-neutral-900 p-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">{item.label}</p>
            <p className="mt-1 text-base font-semibold tabular-nums text-white">{item.value}</p>
          </div>
        ))}
      </div>

      {peakSplit ? (
        <p className="mt-3 text-xs text-neutral-500">
          {t('workout.review.splitNote', {
            label: metricLabels.split_500m_sec.label,
            distance:
              markers.totalDistanceM > 0
                ? t('workout.review.splitDistance', { meters: markers.totalDistanceM })
                : '',
          })}
        </p>
      ) : null}

      {peakHr > 0 ? (
        <div className="mt-4 rounded-xl border border-neutral-800 bg-neutral-900 p-3">
          <p className="text-[10px] uppercase tracking-wider text-neutral-500">{t('workout.review.hrFidelity')}</p>
          <p className="mt-1 text-sm text-neutral-300">
            {t('workout.review.hrFidelityDetail', { count: markers.sampleCount, hr: peakHr })}
          </p>
        </div>
      ) : null}

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-xl bg-kilo-accent py-3 font-semibold text-black transition-colors hover:bg-kilo-accent-dark"
        >
          {t('workout.review.done')}
        </button>
      ) : null}
    </div>
  )
}

export default PostSessionReview
